import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money, num, fmtDate, STATUS_LABELS, STATUS_TONE } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Calculator, Search, ArrowRight, Trash2, Download, UserPlus, LayoutTemplate, Tag, Compass } from "lucide-react";
import { toast } from "sonner";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkBar, BulkBtn, RowCheck } from "@/components/BulkBar";
import { ReassignDialog } from "@/components/ReassignDialog";
import { BidStatusDialog } from "@/components/BidStatusDialog";
import { BulkScopeDialog } from "@/components/BulkScopeDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ResumeRail } from "@/components/ResumeRail";

const ACTIVE = ["estimating", "proposal", "sent"];

export default function Estimates() {
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const sel = useRowSelection();
  const [reassign, setReassign] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplCat, setTplCat] = useState("all");
  const TPL_CATS = [["all", "All"], ["mechanical", "Mechanical"], ["electrical", "Electrical"], ["civil", "Civil"], ["full_epc", "Full EPC"], ["other", "Other"]];
  const inCat = (t) => tplCat === "all" || (t.category || "other") === tplCat;

  const openTemplates = async () => {
    setTplOpen(true); setTplLoading(true);
    try { const r = await api.get("/bid-templates"); setTemplates(r.data || []); }
    catch { toast.error("Could not load templates"); }
    finally { setTplLoading(false); }
  };
  const applyTemplate = async (t) => {
    const name = window.prompt("Name the new estimate (starts as a fresh $0 bid with quantities zeroed):", `${t.name} — new`);
    if (!name) return;
    try {
      const r = await api.post(`/bid-templates/${t.template_id}/spawn`, { name: name.trim() });
      toast.success("Fresh estimate created from template");
      navigate(`/bids/${r.data.bid_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create from template"); }
  };
  const deleteTemplate = async (t, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete template "${t.name}"? Existing estimates are unaffected.`)) return;
    try {
      await api.delete(`/bid-templates/${t.template_id}`);
      setTemplates((list) => list.filter((x) => x.template_id !== t.template_id));
      toast.success("Template deleted");
    } catch { toast.error("Could not delete template"); }
  };

  const exportSel = async () => {
    try {
      const res = await api.post("/bids/export", { bid_ids: sel.ids }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "estimate-estimates.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const doReassign = async (uid) => {
    try {
      const res = await api.post("/bids/bulk", { action: "reassign", bid_ids: sel.ids, assignee_id: uid });
      toast.success(`Reassigned ${res.data.updated} estimate${res.data.updated === 1 ? "" : "s"}`);
      setReassign(false); sel.clear();
    } catch (e) { toast.error(e.response?.data?.detail || "Reassign failed"); }
  };
  const doStatus = async (status) => {
    try {
      const res = await api.post("/bids/bulk", { action: "status", bid_ids: sel.ids, status });
      toast.success(`Updated ${res.data.updated} estimate${res.data.updated === 1 ? "" : "s"}`);
      setStatusOpen(false); sel.clear(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };
  const doScope = async (payload) => {
    try {
      const items = sel.ids.map((id) => ({ kind: "bid", id }));
      const res = await api.post("/pipeline/bulk", { action: "scope", items, ...payload });
      toast.success(`Updated ${res.data.updated} estimate${res.data.updated === 1 ? "" : "s"} · ${res.data.renamed} renamed`);
      setScopeOpen(false); sel.clear(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set scope"); }
  };

  const load = () => api.get("/bids", { params: { historical: false } })
    .then((r) => setBids(r.data.filter((b) => ACTIVE.includes(b.status))))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (b, e) => {
    e.stopPropagation();
    const reason = window.prompt(`Delete "${b.name}"? It leaves the board and every list; only an admin record of who, when and why is kept.\n\nWhy is it being deleted?`);
    if (reason === null) return;
    try {
      await api.delete(`/bids/${b.bid_id}`, { data: { reason } });
      setBids((list) => list.filter((x) => x.bid_id !== b.bid_id));
      toast.success("Estimate deleted");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${sel.count} estimate${sel.count === 1 ? "" : "s"}? This is permanent.`)) return;
    try {
      const res = await api.post("/bids/bulk", { action: "delete", bid_ids: sel.ids });
      toast.success(`Deleted ${res.data.deleted} estimate${res.data.deleted === 1 ? "" : "s"}`);
      sel.clear(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };

  const s = q.trim().toLowerCase();
  const filtered = s ? bids.filter((b) => (b.name || "").toLowerCase().includes(s) || (b.client_name || "").toLowerCase().includes(s)) : bids;
  // Open (in-progress) estimates always sit above submitted ones; within each group the most
  // recently worked / submitted come first, so today's active bids are never buried.
  const OPEN = ["estimating", "proposal"];
  const recency = (b) => b.sent_at || b.submitted_at || b.updated_at || b.created_at || "";
  const byRecent = (a, b) => (recency(b) > recency(a) ? 1 : recency(b) < recency(a) ? -1 : 0);
  const openRows = filtered.filter((b) => OPEN.includes(b.status)).sort(byRecent);
  const sentRows = filtered.filter((b) => !OPEN.includes(b.status)).sort(byRecent);
  const rows = [...openRows, ...sentRows];
  const orderedIds = rows.map((b) => b.bid_id);
  const allSelected = rows.length > 0 && sel.count === rows.length;

  const renderRow = (b, i) => (
    <Card key={b.bid_id} data-testid={`estimate-row-${b.bid_id}`}
      className={`flex items-center gap-4 p-4 transition ${sel.isSelected(b.bid_id) ? "border-[#003E87] bg-[#003E87]/[0.03]" : "hover:border-[#003E87]/40 hover:shadow-sm"}`}>
      <RowCheck checked={sel.isSelected(b.bid_id)} testid={`estimate-check-${b.bid_id}`}
        onClick={(e) => sel.toggle(b.bid_id, i, e.shiftKey, orderedIds)} />
      <Badge className={`${STATUS_TONE[b.status]} hover:${STATUS_TONE[b.status]} shrink-0`}>{STATUS_LABELS[b.status]}</Badge>
      {b.imported && <span data-testid={`estimate-imported-${b.bid_id}`} className="text-[10px] font-semibold uppercase tracking-wide text-[#8a6d00] bg-[#F4C400]/20 rounded px-1.5 py-0.5 shrink-0">Imported</span>}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/bids/${b.bid_id}`)}>
        <div className="text-sm font-medium text-black truncate">{b.name}</div>
        <div className="text-xs text-slate-500 truncate">
          {b.client_name || "No client"} · {num(b.capacity_mw)} MW{b.state ? ` · ${b.state}` : ""}
          {b.status === "sent" && b.sent_at ? ` · sent ${fmtDate(b.sent_at)}` : (b.due_date ? ` · due ${fmtDate(b.due_date)}` : "")}
        </div>
      </div>
      <div className="text-sm font-mono font-medium text-slate-700 shrink-0">{b.stored_total ? money(b.stored_total) : "—"}</div>
      <button data-testid={`estimate-delete-${b.bid_id}`} onClick={(e) => remove(b, e)} title="Delete estimate"
        className="shrink-0 text-slate-300 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
      <ArrowRight className="w-4 h-4 text-slate-300 shrink-0 cursor-pointer" onClick={() => navigate(`/bids/${b.bid_id}`)} />
    </Card>
  );

  const GroupLabel = ({ children, count }) => (
    <div className="flex items-center gap-2 mb-2 mt-1">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</h2>
      <span className="text-xs text-slate-400 font-mono">{count}</span>
    </div>
  );

  return (
    <div className="px-6 sm:px-8 py-8 pb-24">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#003E87]" /> Estimates
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Bids on the board — build the takeoff, price the scope, package the proposal.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openTemplates} data-testid="new-from-template-btn">
            <LayoutTemplate className="w-4 h-4 mr-1.5" /> New from template
          </Button>
          <div className="relative w-64 max-w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="estimates-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an estimate…" className="pl-9" />
          </div>
        </div>
      </div>

      {rows.length > 0 && (
        <div data-testid="estimates-select-all" role="button" tabIndex={0} onClick={() => sel.selectAll(orderedIds)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-3 cursor-pointer">
          <RowCheck checked={allSelected} onClick={() => {}} testid="estimates-select-all-check" />
          {allSelected ? "Clear all" : "Select all"}
        </div>
      )}

      <ResumeRail />

      {loading ? (
        <div className="text-sm text-slate-400 py-16 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <Card className="p-12 text-center text-sm text-slate-500">No estimates in progress. Pursue a lead in the Pipeline to start one.</Card>
      ) : (
        <div className="space-y-6">
          {openRows.length > 0 && (
            <div data-testid="estimates-group-open">
              <GroupLabel count={openRows.length}>In progress</GroupLabel>
              <div className="space-y-2">{openRows.map((b, i) => renderRow(b, i))}</div>
            </div>
          )}
          {sentRows.length > 0 && (
            <div data-testid="estimates-group-submitted">
              <GroupLabel count={sentRows.length}>Submitted</GroupLabel>
              <div className="space-y-2">{sentRows.map((b, i) => renderRow(b, openRows.length + i))}</div>
            </div>
          )}
        </div>
      )}

      <BulkBar count={sel.count} onClear={sel.clear}>
        <BulkBtn testid="estimates-bulk-status" icon={Tag} onClick={() => setStatusOpen(true)}>Set status</BulkBtn>
        <BulkBtn testid="estimates-bulk-discipline" icon={Compass} onClick={() => setScopeOpen(true)}>Set scope</BulkBtn>
        <BulkBtn testid="estimates-bulk-reassign" icon={UserPlus} onClick={() => setReassign(true)}>Reassign</BulkBtn>
        <BulkBtn testid="estimates-bulk-export" icon={Download} onClick={exportSel}>Export</BulkBtn>
        <BulkBtn testid="estimates-bulk-delete" icon={Trash2} tone="danger" onClick={bulkDelete}>Delete</BulkBtn>
      </BulkBar>
      <ReassignDialog open={reassign} onOpenChange={setReassign} count={sel.count} onConfirm={doReassign} />
      <BidStatusDialog open={statusOpen} onOpenChange={setStatusOpen} count={sel.count} onConfirm={doStatus} />
      <BulkScopeDialog open={scopeOpen} onOpenChange={setScopeOpen} items={sel.ids.map((id) => ({ kind: "bid", id }))} onApply={doScope} />

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent data-testid="templates-dialog">
          <DialogHeader>
            <DialogTitle>New estimate from a project template</DialogTitle>
            <DialogDescription>Pick a saved project. It spins up a fresh $0 estimate with the same scope, crews, General Conditions & commercial setup — quantities zeroed, ready for this job's takeoff.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5 mb-1" data-testid="template-cat-filter">
            {TPL_CATS.map(([k, lab]) => (
              <button key={k} onClick={() => setTplCat(k)} data-testid={`tpl-cat-${k}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${tplCat === k ? "bg-[#003E87] text-white border-[#003E87]" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{lab}</button>
            ))}
          </div>
          {tplLoading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : templates.filter(inCat).length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400" data-testid="templates-empty">No templates{tplCat === "all" ? "" : " in this category"} yet. Open a bid and choose “Save as template”.</div>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-auto">
              {templates.filter(inCat).map((t) => (
                <div key={t.template_id} data-testid={`template-${t.template_id}`} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3 hover:border-[#003E87]/40">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-2">{t.name}<span className="text-[10px] uppercase tracking-wide text-[#003E87] bg-[#003E87]/10 rounded px-1.5 py-0.5 shrink-0">{(TPL_CATS.find(([k]) => k === (t.category || "other")) || [null, "Other"])[1]}</span></div>
                    <div className="text-xs text-slate-500 truncate">
                      {t.line_count} scope line{t.line_count === 1 ? "" : "s"}{t.capacity_mw ? ` · ${num(t.capacity_mw)} MW` : ""} · {t.discipline_scope || "both"}{t.alternate_count ? ` · ${t.alternate_count} alternate${t.alternate_count === 1 ? "" : "s"}` : ""}{t.description ? ` · ${t.description}` : ""}
                    </div>
                    {Array.isArray(t.bundles) && t.bundles.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1" data-testid={`template-bundles-${t.template_id}`}>
                        {t.bundles.map((b) => (
                          <span key={b.name} className="inline-flex items-center gap-1 text-[10px] text-[#003E87] bg-[#003E87]/10 rounded px-1.5 py-0.5">
                            {b.name}<span className="font-mono font-semibold">{(b.amount || 0) >= 0 ? "+" : "−"}{money(Math.abs(b.amount || 0))}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button size="sm" onClick={() => applyTemplate(t)} data-testid={`use-template-${t.template_id}`} className="bg-[#003E87] hover:bg-[#00306a]">Use</Button>
                  <button onClick={(e) => deleteTemplate(t, e)} title="Delete template" data-testid={`delete-template-${t.template_id}`} className="text-slate-300 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
